import { ApiService, type PaginatedResponse } from '@sparklayer-io/spark-dash-core/types/api';
import type { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import type { PriceList, PriceListApiResponse, PriceListSource } from '../types';
import { PriceListApi, type PriceListApiContract } from './price-list.api';

export interface PriceListsApiContract {
  priceList(slug: string): PriceListApiContract;

  create(priceList: PriceList): Promise<void>;
  listPaginated(
    sources: PriceListSource[],
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<PriceListApiResponse>>;
  listAllPaginated(
    source: PriceListSource[],
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<PriceListApiResponse>>;
  listAll(): Promise<PriceListApiResponse[]>;
  listAllWithManualPriceCounts(): Promise<PriceListApiResponse[]>;

  bulkUploadCsv(csv: string): Promise<void>;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    priceLists: PriceListsApiContract;
  }
}

/**
 * A service to interact with price lists via the SparkLayer API.
 */
export class PriceListsApi extends ApiService implements PriceListsApiContract {
  public priceList(slug: string): PriceListApiContract {
    return new PriceListApi(this.api, slug);
  }

  public listAll(): Promise<PriceListApiResponse[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/price-lists',
    });
  }

  public async create(priceList: PriceList): Promise<void> {
    await this.api.proxy({
      method: 'POST',
      path: '/v1/price-lists',
      payload: priceList,
    });
  }

  public listPaginated(
    sources: PriceListSource[],
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<PriceListApiResponse>> {
    const params = pagination.toQueryParams();

    params.set('source', sources.join(','));

    return this.api.proxy({
      method: 'GET',
      path: `/v2/price-lists-num-manual-prices?${params}`,
    });
  }

  public listAllPaginated(
    sources: PriceListSource[],
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<PriceListApiResponse>> {
    const params = pagination.toQueryParams();
    params.set('source', sources.join(','));

    return this.api.proxy({
      method: 'GET',
      path: `/v2/price-lists?${params}`,
    });
  }

  public listAllWithManualPriceCounts(): Promise<PriceListApiResponse[]> {
    return this.api.proxy({
      method: 'GET',
      path: '/v1/price-lists-num-manual-prices',
    });
  }

  public bulkUploadCsv(csv: string): Promise<void> {
    return this.api.proxy({
      path: '/v1/batch-update-pricing',
      method: 'PATCH',
      payload: csv,
      contentType: 'text/csv',
    });
  }
}
